import { FieldSize, Size } from '@bms2/shared';
import { Icon } from '../Icon';
import { FieldShell } from './FieldShell';
import styles from './field.module.css';

/**
 * The one control for a fixed set of choices (piece 3; decision 0366). It is
 * a native `<select>` inside the same shell the Text Input uses, so a field
 * that offers five kinds reads like every other field on the form.
 *
 * 🔨 It is not the Dropdown. The Dropdown is a type-ahead over a set nobody
 * can hold in their head — people, places, items — and it searches. A field
 * whose whole list is five words needs no search, and a type-ahead over five
 * words is a worse control, not a fancier one.
 *
 * 🔒 The closed box is the Dropdown's exact box (decision 1450): the same
 * height, corner and chevron, without the magnifier. Only the list a click
 * opens is the browser's.
 *
 * 💰 Sixty-six selects in the application carried a class and looked like the
 * application; fifteen carried none and showed the browser's own chrome, and
 * the product owner met one of those on the Add a way to reach them modal
 * (2026-09-13): *"drop down does not match any other drop down"*.
 */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /**
   * 🔒 The heading the option stands under (decision 8800): options naming a group are drawn after the
   * ungrouped ones, each group under its heading in the order it first appears. The Items list's
   * Attributes heading in the condition rows' field menu is the first.
   */
  group?: string;
}

/** The options with no group, then each group's options under its heading, in the order the groups first appear. */
export function optionGroups(options: readonly SelectOption[]): { loose: SelectOption[]; groups: { label: string; options: SelectOption[] }[] } {
  const groups: { label: string; options: SelectOption[] }[] = [];
  const loose: SelectOption[] = [];
  for (const option of options) {
    if (option.group === undefined) {
      loose.push(option);
      continue;
    }
    const held = groups.find((group) => group.label === option.group);
    if (held) held.options.push(option);
    else groups.push({ label: option.group, options: [option] });
  }
  return { loose, groups };
}

export interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  hint?: string | null;
  /** A first row standing for no choice; absent when the field always holds one. */
  placeholder?: string;
  labelHidden?: boolean;
  /** For a suite or a page that reaches for this field by name. */
  dataAttribute?: Record<string, string>;
  /** Which of the widths the box takes, Column in a line table's cell; the longest option decides it (decisions 0583, 0647). */
  size?: FieldSize;
  /** As wide as this many characters, where that length is exactly known; it wins over `size` (decision 0297). */
  chars?: number;
}

export const selectDocumentedStates = ['default', 'placeholder', 'required', 'error', 'disabled'] as const;

export function Select({ label, value, onChange, options, required = false, disabled = false, error = null, hint = null, placeholder, labelHidden = false, dataAttribute, size = Size.Medium, chars }: SelectProps) {
  const { loose, groups } = optionGroups(options);
  // 🔒 The closed box's tooltip names the whole choice, so a name the box cuts with an ellipsis still reads (piece 3, row T3346).
  const shown = options.find((option) => option.value === value)?.label ?? (value === '' ? placeholder : undefined);
  return (
    <FieldShell label={label} required={required} error={error} disabled={disabled} hint={hint} labelHidden={labelHidden} size={size} {...(chars ? { chars } : {})}>
      {({ inputId, messageId }) => (
        <div className={styles.control}>
          <select
            id={inputId}
            className={styles.input}
            value={value}
            title={shown === undefined || shown === '' ? undefined : shown}
            disabled={disabled}
            required={required}
            aria-describedby={error ? messageId : undefined}
            aria-invalid={error ? true : undefined}
            onChange={(event) => onChange(event.target.value)}
            {...(dataAttribute ?? {})}
          >
            {placeholder === undefined ? null : <option value="">{placeholder}</option>}
            {loose.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
            {groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <SelectChevron />
        </div>
      )}
    </FieldShell>
  );
}

/**
 * The chevron a closed select draws at its right end, the Dropdown's own
 * (decision 1450). A control that composes a native select of its own, such as
 * the Phone Input's country or a list's rows per page, draws it too.
 */
export function SelectChevron() {
  return (
    <span className={styles.selectChevron} aria-hidden="true" data-select-chevron="true">
      <Icon meaning="expand" size="header" />
    </span>
  );
}
